/**
 * Indicateur topbar + panneau des synchronisations refusées (outbox « rejected »).
 *
 * - Compteur discret dans `#sync-rejected-indicator`
 * - Liste consultable (motif FR, modifications conservées)
 * - Copier / exporter (JSON ou CSV) — aucune suppression silencieuse
 *
 * Écoute : `nexxus:sync-rejected` (`SYNC_REJECTED_EVENT`) et rafraîchissement shell
 * (`nexxus:sync-rejected-refresh` sur le root, émis depuis hydrateUser).
 */
import { RemoteSyncStore, type SyncOutboxRow } from '@infra/sync/RemoteSyncStore';
import { SYNC_REJECTED_EVENT } from '@infra/sync/SyncCoordinator';
import { ToastService } from '@presentation/components/Toast';
import { downloadCsv, downloadBlob } from '@presentation/shell/download';
import { esc } from '@presentation/shell/format';
import { trapFocus } from '@shared/a11y/trapFocus';

const PANEL_ID = 'sync-rejected-panel';
const REFRESH_EVENT = 'nexxus:sync-rejected-refresh';

const ACTION_FR: Record<string, string> = {
  create: 'Création',
  update: 'Modification',
  delete: 'Suppression',
};

const SENSITIVE_KEY =
  /password|secret|token|phrase|recovery|clef|key|authorization|credential/i;

export interface SyncRejectedStatusOpts {
  root: HTMLElement;
  getTenantId: () => string | null;
}

function actionLabel(action: string): string {
  return ACTION_FR[action] ?? action;
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function sanitizeValue(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sanitizeValue);
  if (v && typeof v === 'object') return sanitizePayload(v as Record<string, unknown>);
  return v;
}

/** Retire les champs sensibles avant affichage / export UI (objets et tableaux imbriqués inclus). */
function sanitizePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload ?? {})) {
    out[k] = SENSITIVE_KEY.test(k) ? '[masqué]' : sanitizeValue(v);
  }
  return out;
}

function payloadSummary(row: SyncOutboxRow): string {
  try {
    const cleaned = sanitizePayload(row.payload ?? {});
    const raw = JSON.stringify(cleaned);
    if (raw.length <= 280) return raw;
    return `${raw.slice(0, 277)}…`;
  } catch {
    return '—';
  }
}

function motifFr(row: SyncOutboxRow): string {
  const msg = (row.rejected_message ?? '').trim();
  if (msg) return msg;
  return 'Modification refusée par le serveur.';
}

function rowsToCsv(rows: SyncOutboxRow[]): string {
  const header = [
    'id',
    'resource',
    'action',
    'entity_id',
    'motif',
    'code',
    'refused_at',
    'payload',
  ];
  const lines = rows.map((r) => {
    const cells = [
      r.id,
      r.resource,
      r.action,
      r.entity_id,
      motifFr(r),
      r.rejected_code ?? '',
      r.rejected_at ?? '',
      JSON.stringify(sanitizePayload(r.payload ?? {})),
    ];
    return cells.map(csvCell).join(';');
  });
  return [header.join(';'), ...lines].join('\n');
}

function csvCell(value: string): string {
  let s = String(value ?? '');
  // Injection de formule (Excel/LibreOffice) : le motif vient du serveur, donc non fiable.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportableRows(rows: SyncOutboxRow[]): unknown[] {
  return rows.map((r) => ({
    id: r.id,
    resource: r.resource,
    action: r.action,
    entity_id: r.entity_id,
    motif: motifFr(r),
    code: r.rejected_code ?? null,
    refused_at: r.rejected_at ?? null,
    payload: sanitizePayload(r.payload ?? {}),
  }));
}

/**
 * Monte l’indicateur + panneau. Retourne une fonction de démontage.
 * Ouverture de la liste : clic sur `#sync-rejected-indicator` (visible si compteur > 0).
 */
export function mountSyncRejectedStatus(opts: SyncRejectedStatusOpts): () => void {
  const { root, getTenantId } = opts;
  const host = root.querySelector<HTMLElement>('#sync-rejected-indicator');
  if (!host) {
    return () => undefined;
  }

  let rowsCache: SyncOutboxRow[] = [];
  let releaseTrap: (() => void) | null = null;
  let panelOpen = false;

  host.replaceChildren();
  host.hidden = true;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn-sm';
  btn.id = 'sync-rejected-indicator-btn';
  btn.style.cssText =
    'margin-right:8px;background:#B45309;color:#fff;border:none;font-size:11px;font-weight:600;cursor:pointer;';
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-controls', PANEL_ID);

  const live = document.createElement('span');
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('aria-atomic', 'true');
  live.className = 'sync-rejected-live';
  btn.appendChild(live);

  host.appendChild(btn);

  const ensurePanel = (): HTMLElement => {
    let panel = root.querySelector<HTMLElement>(`#${PANEL_ID}`);
    if (panel) return panel;
    panel = document.createElement('div');
    panel.id = PANEL_ID;
    panel.hidden = true;
    panel.style.cssText =
      'position:fixed;inset:0;z-index:10050;background:rgba(10,18,30,0.55);display:flex;align-items:center;justify-content:center;padding:16px;';
    panel.addEventListener('click', (e) => {
      if (e.target === panel) closePanel();
    });
    root.appendChild(panel);
    return panel;
  };

  const closePanel = (): void => {
    const panel = root.querySelector<HTMLElement>(`#${PANEL_ID}`);
    if (!panel) return;
    releaseTrap?.();
    releaseTrap = null;
    panel.hidden = true;
    panel.replaceChildren();
    panelOpen = false;
    btn.setAttribute('aria-expanded', 'false');
  };

  const attachTrap = (panel: HTMLElement): void => {
    const dialog = panel.querySelector<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    releaseTrap?.();
    releaseTrap = trapFocus(dialog, {
      onEscape: (event) => {
        event.preventDefault();
        event.stopPropagation();
        closePanel();
      },
    });
  };

  const renderList = (panel: HTMLElement, rows: SyncOutboxRow[]): void => {
    const card = document.createElement('div');
    card.className = 'card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'sync-rejected-title');
    card.style.cssText =
      'max-width:720px;width:100%;max-height:85vh;overflow:auto;background:#fff;color:#0a121e;border-radius:10px;padding:20px 22px;box-shadow:0 12px 40px rgba(0,0,0,0.25);';

    const empty = rows.length === 0;
    const items = empty
      ? `<p style="margin:0;font-size:13px;color:#2C4A5A;">Aucune synchronisation refusée pour cette entreprise.</p>`
      : rows
          .map((r, idx) => {
            const summary = payloadSummary(r);
            return `<article class="card" style="margin:0 0 10px;padding:12px 14px;border:1px solid #e1e4e8;" data-rejected-idx="${idx}">
              <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;margin-bottom:6px;">
                <strong style="font-size:13px;color:#0a121e;">${esc(actionLabel(r.action))} — ${esc(r.resource)}</strong>
                <span style="font-size:11px;color:#586069;">${esc(formatWhen(r.rejected_at))}</span>
              </div>
              <p style="margin:0 0 6px;font-size:13px;color:#0a121e;"><strong>Motif :</strong> ${esc(motifFr(r))}</p>
              <p style="margin:0 0 6px;font-size:12px;color:#2C4A5A;">Entité : <code>${esc(r.entity_id)}</code></p>
              <p style="margin:0 0 8px;font-size:12px;color:#2C4A5A;word-break:break-word;"><strong>Modifications conservées :</strong> <code style="font-size:11px;">${esc(summary)}</code></p>
              <div style="display:flex;gap:8px;flex-wrap:wrap;">
                <button type="button" class="btn btn-secondary btn-sm" data-copy-one="${idx}">Copier</button>
              </div>
            </article>`;
          })
          .join('');

    card.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:12px;">
        <h2 id="sync-rejected-title" style="margin:0;font-size:16px;color:#0a121e;">Synchronisations refusées</h2>
        <button type="button" class="btn btn-ghost btn-sm" id="sync-rejected-close" aria-label="Fermer">Fermer</button>
      </div>
      <p style="margin:0 0 12px;font-size:12px;color:#2C4A5A;">
        Ces modifications restent enregistrées localement. Elles n’ont pas été appliquées sur le serveur.
      </p>
      <div id="sync-rejected-list">${items}</div>
      ${
        empty
          ? ''
          : `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:14px;padding-top:12px;border-top:1px solid #e1e4e8;">
        <button type="button" class="btn btn-secondary btn-sm" id="sync-rejected-copy-all">Copier tout</button>
        <button type="button" class="btn btn-secondary btn-sm" id="sync-rejected-export-json">Exporter JSON</button>
        <button type="button" class="btn btn-secondary btn-sm" id="sync-rejected-export-csv">Exporter CSV</button>
      </div>`
      }
    `;

    panel.replaceChildren(card);

    card.querySelector('#sync-rejected-close')?.addEventListener('click', () => closePanel());

    card.querySelectorAll<HTMLButtonElement>('[data-copy-one]').forEach((el) => {
      el.addEventListener('click', () => {
        const idx = Number(el.getAttribute('data-copy-one'));
        const row = rows[idx];
        if (!row) return;
        void copyText(JSON.stringify(exportableRows([row])[0], null, 2));
      });
    });

    card.querySelector('#sync-rejected-copy-all')?.addEventListener('click', () => {
      void copyText(JSON.stringify(exportableRows(rows), null, 2));
    });
    card.querySelector('#sync-rejected-export-json')?.addEventListener('click', () => {
      const blob = new Blob([JSON.stringify(exportableRows(rows), null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      downloadBlob(blob, `sync-refusees-${stamp()}.json`);
      ToastService.success('Export JSON téléchargé.');
    });
    card.querySelector('#sync-rejected-export-csv')?.addEventListener('click', () => {
      downloadCsv(rowsToCsv(rows), `sync-refusees-${stamp()}.csv`);
      ToastService.success('Export CSV téléchargé.');
    });
  };

  const openPanel = async (): Promise<void> => {
    await refresh(false);
    const panel = ensurePanel();
    panel.hidden = false;
    renderList(panel, rowsCache);
    panelOpen = true;
    btn.setAttribute('aria-expanded', 'true');
    attachTrap(panel);
  };

  const updateIndicator = (count: number): void => {
    if (count <= 0) {
      host.hidden = true;
      live.textContent = '';
      btn.setAttribute('aria-expanded', 'false');
      btn.title = '';
      btn.setAttribute('aria-label', 'Aucune synchronisation refusée');
      if (panelOpen) closePanel();
      return;
    }
    host.hidden = false;
    const label =
      count === 1
        ? '1 synchronisation refusée'
        : `${count} synchronisations refusées`;
    live.textContent = label;
    btn.title = 'Voir les modifications refusées par le serveur';
    btn.setAttribute('aria-label', `${label}. Ouvrir la liste.`);
  };

  const refresh = async (reloadOpenPanel = false): Promise<void> => {
    const tenantId = getTenantId();
    if (!tenantId) {
      rowsCache = [];
      updateIndicator(0);
      return;
    }
    try {
      rowsCache = await RemoteSyncStore.listRejectedOutbox(tenantId);
      updateIndicator(rowsCache.length);
      if (reloadOpenPanel && panelOpen) {
        const panel = ensurePanel();
        if (!panel.hidden) {
          renderList(panel, rowsCache);
          attachTrap(panel);
        }
      }
    } catch {
      /* pas de log de payload / secrets */
      rowsCache = [];
      updateIndicator(0);
    }
  };

  const onRejectedEvent = (ev: Event): void => {
    const detail = (ev as CustomEvent<{ tenantId?: string; rejected?: number }>).detail;
    const active = getTenantId();
    if (!active) return;
    if (detail?.tenantId && detail.tenantId !== active) return;
    void refresh(panelOpen);
  };

  const onShellRefresh = (): void => {
    void refresh(panelOpen);
  };

  const onTenantSelect = (ev: Event): void => {
    const t = ev.target;
    if (t instanceof HTMLSelectElement && t.id === 'tenant-select') {
      void refresh(false);
    }
  };

  btn.addEventListener('click', () => {
    if (panelOpen) {
      closePanel();
      return;
    }
    void openPanel();
  });

  window.addEventListener(SYNC_REJECTED_EVENT, onRejectedEvent);
  root.addEventListener(REFRESH_EVENT, onShellRefresh);
  root.addEventListener('change', onTenantSelect);

  void refresh(false);

  return () => {
    closePanel();
    window.removeEventListener(SYNC_REJECTED_EVENT, onRejectedEvent);
    root.removeEventListener(REFRESH_EVENT, onShellRefresh);
    root.removeEventListener('change', onTenantSelect);
    host.replaceChildren();
    host.hidden = true;
    root.querySelector(`#${PANEL_ID}`)?.remove();
  };
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    ToastService.success('Copié dans le presse-papiers.');
  } catch {
    ToastService.error('Impossible de copier. Réessayez ou utilisez Exporter.');
  }
}

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}
